interface OpsPropertyDetail {
  property_id: string
  property_name: string
  host_email: string
  pms_health: { status: string; provider: string; connected: boolean }
  calendar_health: { status: string; last_sync_at: string; next_booking: { start_date: string; end_date: string; booking_number: string; summary: string } | null }
  places_health: { status: string; count: number }
  display_health: { status: string; last_heartbeat: string; device_id: string }
  guidebook_health: { status: string; enabled: boolean; last_login_at: string }
  controls: { updates_disabled: boolean; llm_disabled: boolean; updated_at: string; updated_by: string }
  key_ids: {
    property_id: string
    primary_listing_id: string
    primary_listing_name: string
    render_key: string
    pms_property_ref: string
  }
  latest_activity: Array<{ ts: string; type: string; label: string; status: string; message: string }>
}

export function OpsPropertyDrawer({
  property,
  isLoading,
  onClose,
  onAction,
  actionBusy,
  onCopy,
}: {
  property: OpsPropertyDetail | null
  isLoading: boolean
  onClose: () => void
  onAction: (actionType: string, payload?: Record<string, unknown>) => void
  actionBusy: string
  onCopy: (value: string) => void
}) {
  if (!property && !isLoading) return null

  return (
    <div className="drawer-overlay" onClick={onClose} role="presentation" aria-hidden="true">
      <div className="drawer drawer--wide" onClick={(event) => event.stopPropagation()}>
        <div className="drawer__header">
          <h3 className="drawer__title">
            {property?.property_name || "Property"}
          </h3>
          <button className="drawer__close" type="button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {isLoading || !property ? (
          <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
            <div className="sf-spinner" style={{ margin: "0 auto" }} />
          </div>
        ) : (
          <div className="drawer__body">
            <div className="ops-drawer-section">
              <div className="ops-drawer-section__title">Key IDs</div>
              <DrawerField label="Property ID" value={property.key_ids.property_id} onCopy={onCopy} />
              <DrawerField label="Primary Listing" value={property.key_ids.primary_listing_id || "—"} onCopy={onCopy} />
              <DrawerField label="Render Key" value={property.key_ids.render_key || "—"} onCopy={onCopy} />
            </div>

            <div className="ops-drawer-section">
              <div className="ops-drawer-section__title">Health Checklist</div>
              <HealthLine label="PMS" status={property.pms_health.status} detail={property.pms_health.provider || "unknown"} />
              <HealthLine label="Calendar" status={property.calendar_health.status} detail={property.calendar_health.last_sync_at || "No sync"} />
              <HealthLine label="Display" status={property.display_health.status} detail={property.display_health.last_heartbeat || "No heartbeat"} />
              <HealthLine label="Guidebook" status={property.guidebook_health.status} detail={property.guidebook_health.enabled ? "Enabled" : "Disabled"} />
            </div>

            <div className="ops-drawer-section">
              <div className="ops-drawer-section__title">Safety Controls</div>
              <ToggleRow
                label="Disable outgoing updates"
                checked={property.controls.updates_disabled}
                busy={actionBusy === "set_updates_disabled"}
                onToggle={(checked) => onAction("set_updates_disabled", { disabled: checked })}
              />
              <ToggleRow
                label="Disable LLM calls (fallback only)"
                checked={property.controls.llm_disabled}
                busy={actionBusy === "set_llm_disabled"}
                onToggle={(checked) => onAction("set_llm_disabled", { disabled: checked })}
              />
            </div>

            <div className="ops-drawer-section">
              <div className="ops-drawer-section__title">Quick Actions</div>
              <div className="ops-action-grid">
                <ActionButton label="Trigger PMS Sync" busy={actionBusy === "trigger_pms_sync"} onClick={() => onAction("trigger_pms_sync")} />
                <ActionButton label="Trigger Calendar Sync" busy={actionBusy === "trigger_calendar_sync"} onClick={() => onAction("trigger_calendar_sync")} />
                <ActionButton label="Trigger Render" busy={actionBusy === "trigger_render"} onClick={() => onAction("trigger_render")} />
                <ActionButton label="Rotate Guidebook Key" busy={actionBusy === "rotate_guidebook_key"} onClick={() => onAction("rotate_guidebook_key")} />
                <ActionButton label="Revoke Guidebook Sessions" busy={actionBusy === "revoke_guidebook_sessions"} onClick={() => onAction("revoke_guidebook_sessions")} />
              </div>
            </div>

            <div className="ops-drawer-section">
              <div className="ops-drawer-section__title">Latest Activity</div>
              <div className="ops-activity-list">
                {property.latest_activity.length > 0 ? (
                  property.latest_activity.map((item, index) => (
                    <div className="ops-activity-row" key={`${item.ts}-${index}`}>
                      <div className="ops-activity-row__time">{formatTime(item.ts)}</div>
                      <div className="ops-activity-row__main">
                        <span className={`status-pill status-pill--${item.status === "failed" || item.status === "error" ? "error" : item.status === "warning" ? "warning" : "healthy"}`}>
                          <span className="status-pill__dot" />
                          {item.label}
                        </span>
                        <div className="ops-activity-row__message">{item.message || "—"}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="ops-activity-row__message">No recent activity</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ActionButton({
  label,
  busy,
  onClick,
}: {
  label: string
  busy: boolean
  onClick: () => void
}) {
  return (
    <button className="data-table__action" type="button" disabled={busy} onClick={onClick}>
      {busy ? "Running..." : label}
    </button>
  )
}

function DrawerField({
  label,
  value,
  onCopy,
}: {
  label: string
  value: string
  onCopy: (value: string) => void
}) {
  return (
    <div className="drawer__field">
      <span className="drawer__field-label">{label}</span>
      <span className="drawer__field-value">
        {value}
        {value && value !== "—" && (
          <button className="drawer__copy-btn" type="button" onClick={() => onCopy(value)}>
            Copy
          </button>
        )}
      </span>
    </div>
  )
}

function HealthLine({
  label,
  status,
  detail,
}: {
  label: string
  status: string
  detail: string
}) {
  const cls = status === "healthy" ? "healthy" : status === "error" ? "error" : status === "warning" ? "warning" : "neutral"
  return (
    <div className="ops-health-line">
      <div className="ops-health-line__label">{label}</div>
      <div className="ops-health-line__value">
        <span className={`status-pill status-pill--${cls}`}>
          <span className="status-pill__dot" />
          {status}
        </span>
        <span className="ops-health-line__detail">{detail}</span>
      </div>
    </div>
  )
}

function ToggleRow({
  label,
  checked,
  busy,
  onToggle,
}: {
  label: string
  checked: boolean
  busy: boolean
  onToggle: (next: boolean) => void
}) {
  return (
    <label className="ops-toggle-row">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        disabled={busy}
        onChange={(event) => onToggle(event.target.checked)}
      />
    </label>
  )
}

function formatTime(value: string) {
  const iso = value || ""
  if (!iso) return "—"
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return iso
  return new Date(ts).toLocaleString()
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M5 5l8 8M13 5l-8 8" />
    </svg>
  )
}
